import { APP_INITIALIZER, NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { CookieService } from 'ngx-cookie-service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';

import packageJson from '../../package.json';
import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthGuard } from './shared';
import { AuthenticationService } from './shared/services/authentication.service';
import { ApiService } from './shared/services/api.service';
import { BarService } from './shared/services/charts/bar.service';
import { ColumnrangeService } from './shared/services/charts/columnrange.service';
import { ColumnService } from './shared/services/format/column.service';
import { FlashMessagesModule } from './shared/modules/flash-messages/module';
import { CsvExporterModule } from './shared/modules/csv-exporter/module';
import { CoreModule } from './core/core.module';
import { IsSuperOpsGuard } from './shared/guard/is-super-ops.guard';
import { LookAndFeelService } from './shared/services/look-and-feel.service';
import { InstanceService } from './shared/services/instance.service';
import { EuiccsService } from './shared/services/euiccs.service';
import { AddProfileService } from './layout/euiccs/pools/add-profiles/add-profile-service';
import { SessionService } from './shared/services/session.service';

export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${packageJson.version}`);
}

@NgModule({
    imports: [
        CommonModule,
        BrowserModule,
        FormsModule,
        BrowserAnimationsModule,
        HttpClientModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
        FlashMessagesModule,
        CsvExporterModule,
        AppRoutingModule,
        HighchartsChartModule,
        CoreModule,
        NgxSpinnerModule,
        ReactiveFormsModule
    ],
    declarations: [AppComponent],
    providers: [
        DatePipe,
        AuthGuard,
        AuthenticationService,
        IsSuperOpsGuard,
        CookieService,
        ColumnService,
        ColumnrangeService,
        ApiService,
        BarService,
        InstanceService,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeHighChartsOptions,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initTranslationsService,
            multi: true,
            deps: [ApiService, CookieService, TranslateService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initAppCenterAsssetsCookies,
            multi: true,
            deps: [CookieService, InstanceService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initConfig,
            multi: true,
            deps: [CookieService, InstanceService, LookAndFeelService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initLookAndFeel,
            multi: true,
            deps: [CookieService, HttpClient, LookAndFeelService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initRspm,
            multi: true,
            deps: [SessionService, EuiccsService, AddProfileService ]
        }
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}


export function initializeHighChartsOptions(){
    return () => {
        Highcharts.setOptions({
            lang:{
                thousandsSep:"",
                accessibility:{
                    thousandsSep:""
                }
            }
        });
    }
}

export function initTranslationsService(apiService: ApiService, cookieService: CookieService, translate: TranslateService ){
    return () => {

        const browserLang = translate.getBrowserLang();
        translate.addLangs(['en_US']);
        translate.setDefaultLang('en_US');
        translate.use('en_US');

        if (!cookieService.check("app-center-sessionId"))
            return;
        apiService.getData("locale.get", {}).subscribe(result => {
            if(result){
                cookieService.set(
                    'app-center-lang', result.locale,
                    9999, '/',  window.location.hostname,
                    true, "Strict"
                );

                if(translate.currentLang !== result.locale) translate.use(result.locale);
            }
        });
    }
}

export function initAppCenterAsssetsCookies(cookieService: CookieService, instanceService: InstanceService){
    return() => {
        let prefix = cookieService.get("app-center-instance");
        if(prefix && environment.instances[prefix]){
            document.cookie = `app-center-instances-url=${instanceService.appCenterInstanceUrl};path=/;domain=${window.location.hostname};secure`;
            document.cookie = `app-center-current-instance=${instanceService.appCenterCurrentInstance};path=/;domain=${window.location.hostname};secure`;
            document.cookie = `app-center-endpoint=${instanceService.appCenterEndpoint};path=/;domain=${window.location.hostname};secure`;
            document.cookie = `app-center-url=${instanceService.appCenterUrl};path=/;domain=${window.location.hostname};secure`;
       }
    }
}

export function initConfig(cookieService: CookieService, instanceService: InstanceService, lookAndFeelService: LookAndFeelService){
    return() => {
        let instance = cookieService.get("app-center-instance");
        if(instance && environment.instances[instance]){
            const platformInstances = environment.instances;
            localStorage.setItem("app-center-config-object", JSON.stringify(platformInstances));
            (typeof platformInstances === 'object') ?
                localStorage.setItem("app-center-config", JSON.stringify(platformInstances[instance])):
                localStorage.setItem("app-center-config", JSON.stringify(JSON.parse(JSON.stringify(platformInstances)).find((item: any) => item.key === instance)));
        }
    }
}

export function initLookAndFeel(cookieService: CookieService, http: HttpClient, lookAndFeelService: LookAndFeelService){
    return() => {
        let instance = cookieService.get("app-center-instance");
        if(instance && environment.instances[instance]){
            const lnfUrl = environment.instances[instance].lnfUrl;
            if(lnfUrl && lnfUrl !== ""){
                http
                    .get(decodeURI(lnfUrl.replace(/['"]+/g, "")))
                    .pipe(
                        catchError( err=> {console.log(err); return of(null)})
                    )
                    .subscribe(resp => {
                        if(resp && resp?.landf) {
                            localStorage.setItem("app-center-lnf", JSON.stringify(resp?.landf));

                            lookAndFeelService.setLookAndFeel(resp?.landf)
                        }
                    });
            }
        }
    }
}

export function initRspm(sessionService: SessionService, euiccsService: EuiccsService, addProfileService: AddProfileService){
    return () => euiccsService.rspmEnabled$.pipe(
        // Wait until RSPM is enabled (skip initial false)
        filter(Boolean),
        // Only consider the first enable/disable value and then complete
        take(1),
        switchMap((enabled) =>
            enabled ? sessionService.session$.pipe(take(1)) : of(null)
        ),
        tap((session) => {
          // Guard: only call when we have a real session
          if (session && session.orgId) {
            addProfileService.startInProgessCampaign(session.orgId);
          }
        })
    )
    .subscribe({
        // Optional: add minimal error handling
        error: (err) => console.warn('initRspm error (non-blocking):', err),
    });
}
